import { PreferenceSection } from 'components/Settings';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormLabel, RichTextArea, Stack, Text } from 'shell/components';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';
import { usePreferenceSettings } from '../../Preferences';

const GradingInstructionsField = ({ id, label, description, ...props }) => {
    const descriptionId = description ? `${id}-description` : undefined;

    return (
        <Stack spacing={2}>
            <FormLabel htmlFor={id} sx={{ fontWeight: 500 }}>
                {label}
            </FormLabel>
            {description && (
                <Text color='light' id={descriptionId} variant='p2'>
                    {description}
                </Text>
            )}
            <RichTextArea aria-describedby={descriptionId} aria-label={label} id={id} {...props} />
        </Stack>
    );
};

const Grading = ({ sectionId }) => {
    const { defaults, update, adminOverrideStates } = usePreferenceSettings();
    const { t } = useTranslation('assignment');
    const [context, setContext] = useState(defaults.gradingCustomContext);

    useEffect(() => {
        setContext(defaults.gradingCustomContext);
    }, [defaults.gradingCustomContext]);

    return (
        <PreferenceSection
            id={sectionId}
            title={t('grading-section.title', 'Grading')}
            description={t(
                'settings.grading.description',
                'Set your default AI grading preferences. These settings apply across all assignments and can be overridden by assignment-specific grading settings.'
            )}
            toggleLabel={t('settings.grading.toggle-label', 'AI Suggested Grading')}
            toggleDescription={t(
                'settings.grading.toggle-description',
                'TimelyGrader uses assignment details and rubric to suggest grades and feedback on student submissions.'
            )}
            enabled={defaults.aiGradingEnabled}
            adminOverrideState={adminOverrideStates[sectionId]}
            disabled={false}
            onToggle={(value) => update('aiGradingEnabled', value)}
        >
            <Stack spacing={3}>
                <GradingInstructionsField
                    id='grading-instructions'
                    label={t('settings.grading.instructions-title', 'General Grading Instructions')}
                    description={t(
                        'settings.grading.instructions-description',
                        'Provide additional context to help AI generate more relevant grades and feedback.'
                    )}
                    placeholder={t(
                        'settings.grading.instructions-placeholder',
                        'Example: Be flexible with minor grammar or spelling issues unless they affect meaning. Prioritize whether students understand the concepts, explain their reasoning clearly, and meet the rubric expectations.'
                    )}
                    value={context}
                    helperText={`${getHTMLWordCount(context)}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT} words`}
                    helperTextPosition={'end'}
                    onChange={setContext}
                    onBlur={(value) => {
                        setContext(value);
                        update('gradingCustomContext', value);
                    }}
                />
            </Stack>
        </PreferenceSection>
    );
};

export default Grading;
